import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AnswerSubCategoryDetail } from "@/schema/app/assessment/answerSubCategoryDetail";
import BottomBar from "@/src/app/(auth)/assessment/categories/[id]/_components/BottomBar";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ActionButtons from "./ActionButtons";
import { useSubCategoryActions } from "../_hooks/useSubCategoryActions";

interface SubCategoryBottomBarProps {
  backLink: string;
  previousLink: string;
  nextLink: string;
  subCategoryId: string;
  refetch: () => void;
}

function SubCategoryBottomBar({
  backLink,
  previousLink,
  nextLink,
  subCategoryId,
  refetch,
}: SubCategoryBottomBarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formState, control } = useFormContext<AnswerSubCategoryDetail>();
  const isSubmitted = useWatch({ control, name: "is_submitted" });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  const { saveDraft, markCompleted, isSaving, isMarking } =
    useSubCategoryActions({
      subCategoryId,
      refetch,
    });

  const isBusy = isSaving || isMarking;

  const handleNavigate = (
    link: string,
    source: "back" | "previous" | "next",
  ) => {
    if (!link) {
      return;
    }
    if (!formState.isDirty || source === "back") {
      navigate(link);
      return;
    }
    setPendingLink(link);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setPendingLink(null);
  };

  const proceedToLink = () => {
    if (pendingLink) {
      navigate(pendingLink);
    }
    closeDialog();
  };

  const handleSaveDraft = async () => {
    const didSave = await saveDraft();
    if (didSave) {
      proceedToLink();
    }
  };

  const handleMarkCompleted = async () => {
    const didMark = await markCompleted();
    if (didMark) {
      proceedToLink();
    }
  };

  return (
    <>
      <BottomBar
        backLink={backLink}
        previousLink={previousLink}
        nextLink={nextLink}
        onNavigate={handleNavigate}
        children={
          <ActionButtons
            actions={{
              saveDraft,
              markCompleted,
              isSaving,
              isMarking,
            }}
          />
        }
      />
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
            return;
          }
          setIsDialogOpen(true);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("unsaved-changes-title")}</DialogTitle>
          </DialogHeader>
          <div className="grid w-full gap-2 p-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              {t("unsaved-changes-description")}
            </div>
            <Button
              className="w-full"
              variant="secondary"
              isLoading={isSaving}
              disabled={isBusy || isSubmitted}
              onClick={handleSaveDraft}
            >
              {t("save-draft")}
            </Button>
            <Button
              className="w-full"
              isLoading={isMarking}
              disabled={isBusy || isSubmitted}
              onClick={handleMarkCompleted}
            >
              {t("mark-as-completed")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SubCategoryBottomBar;